import { useAuth } from "@clerk/react";
import { Spinner } from "@/components/ui/spinner";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import CampaignTabs from "@/components/dashboard_campaigns/campaign-tabs";
import {
  ArrowLeftIcon,
  ChartLineIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  // FileVideoIcon,
  // Inbox,
  ListOrderedIcon,
  Notebook,
  RefreshCw,
  Settings,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { useAnalyticsBarStore, useCampaignStore, useCreatorsStore } from "@/stores";
import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import HelpDialog from "@/components/ui/VideoHelper";
import AmyBadge from "@/components/ui/amy-badge";
import { isEveCampaign } from "@/lib/eve";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function isTabActive(pathname: string, tabId: string) {
  // Convert everything to lowercase for case-insensitive comparison
  const lowerPathname = pathname.toLowerCase();
  const lowerTabId = tabId.toLowerCase();

  // Split the pathname into segments
  const segments = lowerPathname.split("/").filter(Boolean);

  // Find the position of "campaigns" in the URL
  const campaignsIndex = segments.indexOf("campaigns");

  if (campaignsIndex !== -1 && segments.length >= campaignsIndex + 3) {
    // After "campaigns", there should be a campaign ID, then the tab ID
    const currentTabSegment = segments[campaignsIndex + 2];

    // Check if the current tab segment matches the tab ID
    return currentTabSegment === lowerTabId;
  }

  return false;
}

export default function CampaignPage() {
  const { campaign_id } = useParams();
  const { orgId } = useAuth();
  const { pathname } = useLocation();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const {
    campaign,
    isLoading: campaignLoading,
    error: campaignError,
    fetchCampaign,
  } = useCampaignStore();
  const { fetchCreators } = useCreatorsStore();
  const { getCampaignDubAnalytics } = useAnalyticsStore();
  const { showAnalyticsBar, toggleAnalyticsBar } = useAnalyticsBarStore();

  const tabs = [
    {
      id: "creators",
      label: "Creators",
      icon: (
        <User
          className="-ms-0.5 me-1.5"
          size={16}
          aria-hidden="true"
        />
      ),
      path: "/creators",
      isActive: isTabActive(pathname, "creators"),
    },

    {
      id: "brief",
      label: "Brief",
      icon: (
        <Notebook
          className="-ms-0.5 me-1.5"
          size={16}
          aria-hidden="true"
        />
      ),
      path: "/brief",
      isActive: isTabActive(pathname, "brief"),
    },
    // {
    //   id: "inbox",
    //   label: "Inbox",
    //   icon: (
    //     <Inbox
    //       className="-ms-0.5 me-1.5"
    //       size={16}
    //       aria-hidden="true"
    //     />
    //   ),
    //   path: "/inbox",
    //   isActive: isTabActive(pathname, "inbox"),
    // },
    // {
    //   id: "drafts",
    //   label: "Drafts",
    //   icon: (
    //     <FileVideoIcon
    //       className="-ms-0.5 me-1.5"
    //       size={16}
    //       aria-hidden="true"
    //     />
    //   ),
    //   path: "/drafts",
    //   isActive: isTabActive(pathname, "drafts"),
    // },
    {
      id: "stages",
      // Eve campaigns have no legacy stages — the same route renders the
      // read-only Amy pipeline instead (StagesTab branches on is_eve).
      label: isEveCampaign(campaign) ? "Pipeline" : "Stages",
      icon: (
        <ListOrderedIcon
          className="-ms-0.5 me-1.5"
          size={16}
          aria-hidden="true"
        />
      ),
      path: "/stages",
      beta: false,
      isActive: isTabActive(pathname, "stages"),
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: (
        <ChartLineIcon
          className="-ms-0.5 me-1.5"
          size={16}
          aria-hidden="true"
        />
      ),
      path: "/analytics",
      isActive: isTabActive(pathname, "analytics"),
    },
    {
      id: "settings",
      label: "Settings",
      icon: (
        <Settings
          className="-ms-0.5 me-1.5"
          size={16}
          aria-hidden="true"
        />
      ),
      path: "/settings",
      isActive: isTabActive(pathname, "settings"),
    },
  ];

  async function handleRefreshData() {
    const token = await getToken();
    if (!token || !campaign_id || !orgId) {
      console.error("No token found");
      return;
    }
    setLoadingRefresh(true);
    try {
      await fetchCampaign(campaign_id, orgId, token);
      await fetchCreators(campaign_id, token);
      await getCampaignDubAnalytics(campaign_id, token);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      // toast.success("Data refreshed successfully!");
      setLoadingRefresh(false);
    }
  }

  useEffect(() => {
    if (campaign_id && orgId) {
      const fetchData = async () => {
        try {
          const token = await getToken();
          if (token) {
            await Promise.all([
              fetchCampaign(campaign_id, orgId, token),
              fetchCreators(campaign_id, token),
              getCampaignDubAnalytics(campaign_id, token),
            ]);
          } else {
            console.error("Error: Token is null");
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }
  }, [campaign_id, orgId, fetchCampaign, fetchCreators, getCampaignDubAnalytics, getToken]);

  if (campaignLoading || !campaign) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner className="size-4" />
      </div>
    );
  }

  if (campaignError) {
    return (
      <div className="w-full h-full flex flex-col gap-2 text-sm justify-center items-center">
        Error loading campaign: {campaignError}
        <Button onClick={() => window.location.reload()} variant="outline">
          <RefreshCw className="size-4" /> Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex items-center md:flex-row flex-col md:min-h-12 min-h-22 justify-between w-full border-b pl-2.5 pr-2 bg-chrome">
        <Button
          size="icon-sm"
          variant="muted-ghost"
          className="mr-2 lg:flex hidden"
          onClick={() => navigate("/dashboard/campaigns")}
        >
          <ArrowLeftIcon />
        </Button>
        <div className="lg:text-xl text-lg font-semibold flex flex-1 items-center gap-2 group min-w-0">
          <h1 className="truncate">{campaign.campaign_name}</h1>
          {isEveCampaign(campaign) && (
            <Tooltip>
              <TooltipTrigger className="shrink-0 cursor-help">
                <AmyBadge className="text-sm font-medium bg-background p-0.5 pr-1.5" imgClassName="size-6" />
              </TooltipTrigger>
              <TooltipContent>
                Run by Amy — manage this campaign from the Amy chat.
              </TooltipContent>
            </Tooltip>
          )}
          <HelpDialog
            featureId="campaign-outreach"
            variant="ghost"
            size="icon"
            title="Start Outreach"
            description="This video will guide you through the process of starting outreach for your campaign. It covers how to create a brief, initiate contact, and manage responses."
            videoUrl="https://www.youtube.com/embed/7Fraky4EBvs?si=WuCXtS1ruWu0_XYE"
            className="lg:flex hidden shrink-0 opacity-0 group-hover:opacity-100"
          />
          <CopyButton
            buttonText=""
            tooltipText="Copy Campaign ID"
            textToCopy={campaign_id || ""}
            variant="ghost"
            className="size-7.5 text-muted-foreground hover:bg-muted-foreground/10 opacity-0 group-hover:opacity-100 lg:flex hidden shrink-0"
            tooltipDelay={2000}
            tooltipSide="right"
          />
          <Button
            size="icon-md"
            variant="muted-ghost"
            className="opacity-0 group-hover:opacity-100 lg:flex hidden shrink-0"
            onClick={() => handleRefreshData()}
            disabled={loadingRefresh}
          >
            {!loadingRefresh && <RefreshCw />}
            {loadingRefresh && <Spinner />}
          </Button>
        </div>
        <div className="flex items-center gap-1 group">
          {/* AnalyticsBar toggle — only where the bar exists (Creators tab);
              hover-revealed like the title row's copy/refresh buttons. */}
          {isTabActive(pathname, "creators") && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-md"
                  variant="muted-ghost"
                  className="opacity-0 group-hover:opacity-100 lg:flex hidden shrink-0"
                  onClick={toggleAnalyticsBar}
                >
                  {showAnalyticsBar ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {showAnalyticsBar ? "Hide analytics bar" : "Show analytics bar"}
              </TooltipContent>
            </Tooltip>
          )}
          <CampaignTabs tabs={tabs} />
        </div>
      </div>
      <div className="h-full w-full overflow-auto">
        {/* Tabs remount on every path change — except Settings and Pipeline
            (stages), which stay mounted across their sections (per campaign)
            so SettingsLayout can animate one section out and the next in. */}
        <Outlet
          key={
            isTabActive(pathname, "settings")
              ? `settings:${campaign_id}`
              : isTabActive(pathname, "stages")
                ? `stages:${campaign_id}`
                : pathname
          }
        />
      </div>
    </div>
  );
}